import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, Users, Zap, Clock, Loader2, RefreshCw } from "lucide-react";
import { LiveControl } from "@/components/admin/LiveControl";

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="border border-[#222] bg-[#0F0F0F] p-4 rounded-sm">
    <div className="flex items-center gap-2 text-[#888] text-xs font-mono-term uppercase tracking-widest">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className={`font-heading text-2xl font-bold mt-1 ${tone || "text-white"}`}>{value}</div>
  </div>
);

export function AdminCpiControl() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cpi-control");
      setData(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#666]" /></div>;
  if (!data) return <p className="text-sm text-[#888] py-8">Unable to load CPI control.</p>;

  const cpi = data.cpi || {};
  const dot = { green: "#22e07b", yellow: "#f5c518", red: "#ff5b5b" }[cpi.color] || "#22e07b";

  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-cpi-control">
      <div className="flex items-center gap-2">
        <Activity className="h-5 w-5" style={{ color: dot }} />
        <span className="font-heading text-lg font-semibold">CPI AI Control</span>
        <button onClick={load} data-testid="cpi-control-refresh" className="ml-auto text-[#888] hover:text-white">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Users} label="Total Clients" value={data.total_clients} />
        <Stat icon={Zap} label="Activated" value={data.ai_activated} tone="text-[#22e07b]" />
        <Stat icon={Clock} label="Waiting" value={data.waiting} tone="text-[#f5c518]" />
        <Stat icon={Activity} label="Engine Status" value={data.ai_status} />
      </div>

      <div className="border border-[#222] bg-[#0F0F0F] p-5 rounded-sm">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: dot, boxShadow: `0 0 10px ${dot}` }} />
          <span className="font-heading font-semibold">{cpi.event_title || "USD CPI Release"}</span>
          <span className="ml-auto font-mono-term text-xs uppercase tracking-widest px-2 py-0.5 border border-[#222] text-[#888]">
            {data.market_condition}
          </span>
        </div>
        <div className="text-sm text-[#aaa] mt-2">{cpi.label} · {cpi.scheduled_et || "—"}</div>
        <div className="flex flex-wrap gap-2 mt-3">
          {(cpi.markets || []).map((m) => (
            <span key={m} className="px-2.5 py-1 rounded-sm border border-[#222] text-xs font-mono-term text-[#888]">{m}</span>
          ))}
        </div>
      </div>

      <LiveControl />
    </div>
  );
}
